'use client';

import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface PricingOTPFormProps {
  email: string;
  otp: string;
  onOtpChange: (otp: string) => void;
  onVerify: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
  message: string | null;
  error: string | null;
}

export function PricingOTPForm({
  email,
  otp,
  onOtpChange,
  onVerify,
  onBack,
  isLoading,
  message,
  error,
}: PricingOTPFormProps) {
  const t = useTranslations('pricing');

  return (
    <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-800 dark:bg-gray-900">
      <form onSubmit={onVerify} className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('otp.title') || 'Enter Verification Code'}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('otp.description') || 'We just sent a 6-digit code to'}{' '}
            <span className="font-semibold">{email}</span>.
          </p>
        </div>

        {message && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-500/60 dark:bg-green-500/10 dark:text-green-200">
            <div className="flex items-start gap-2">
              <CheckCircle className="mt-0.5 h-5 w-5" />
              <span>{message}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          value={otp}
          onChange={(e) => onOtpChange(e.target.value.replace(/[^0-9]/g, ''))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-lg tracking-[0.4em] text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-950 dark:text-primary-100"
          placeholder={t('otp.placeholder') || '000000'}
        />
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={otp.length !== 6 || isLoading}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40"
          >
            {isLoading ? t('otp.verifying') || 'Verifying...' : t('otp.verify') || 'Verify code'}
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={onBack}
            className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition-all duration-300"
          >
            {t('otp.back') || 'Back'}
          </button>
        </div>
      </form>
    </div>
  );
}

